// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';

import type { BffWebSocketClient } from '@/services/bffWebSocket';
import type { ViewerRouteParams } from '@/hooks/useInviteViewer';
import { withViewerParams } from '@/services/tourInviteLink';
import { playIndex } from '@/utils/holeOrder';

type UseHoleActionsParams = {
  debug: boolean;
  holeNumber: number;
  /** The hole the round started on; drives the slide direction across the 18 -> 1 wrap. */
  startHole?: number | null;
  holesCount?: number;
  roundId: string | null;
  canMutateRoundStatus: boolean;
  /** Keeps a spectator spectating when they step to the next hole; empty for everyone else. */
  viewerRouteParams: ViewerRouteParams;
  router: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
  selectedGameDefinitionIds: string[];
  setHole: (hole: number) => void;
  /**
   * Told about every navigation; `passive` is true when the screen follows another device or
   * the persisted position rather than the user's own tap (cross-device hole sync).
   */
  onNavigate?: (targetHole: number, passive: boolean) => void;
  socketClient: BffWebSocketClient | null;
  logWithContext: (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => void;
};

export const useHoleActions = ({
  debug,
  holeNumber,
  startHole = null,
  holesCount = 18,
  roundId,
  canMutateRoundStatus,
  viewerRouteParams,
  router,
  selectedGameDefinitionIds,
  setHole,
  onNavigate,
  socketClient,
  logWithContext,
}: UseHoleActionsParams) => {
  const navigateToHole = useCallback(
    (targetHole: number, options?: { passive?: boolean }) => {
      if (!roundId) return;
      onNavigate?.(targetHole, options?.passive === true);
      setHole(targetHole);
      const params: string[] = [];
      if (debug) params.push('debug=true');
      if (selectedGameDefinitionIds.length > 0) {
        params.push(`gameDefinitionIds=${encodeURIComponent(selectedGameDefinitionIds.join(','))}`);
      }
      // Compared in play order so the 18 -> 1 wrap of a back-nine start animates forward.
      const dir = playIndex(targetHole, startHole, holesCount) > playIndex(holeNumber, startHole, holesCount) ? 1 : -1;
      params.push(`dir=${dir}`);
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(withViewerParams(`/round/${roundId}/hole/${targetHole}/compact${query}`, viewerRouteParams));
    },
    [debug, holeNumber, holesCount, onNavigate, roundId, router, selectedGameDefinitionIds, setHole, startHole, viewerRouteParams]
  );

  const finishRound = useCallback(() => {
    if (!socketClient || !roundId) return;

    if (canMutateRoundStatus) {
      // 'queued' is not a failure — the frame is flushed when the socket (re)opens.
      const sent = socketClient.send({ type: 'round.status.update', roundId, status: 'finished' });
      if (sent !== 'sent') {
        logWithContext('hole.round.finishQueued', { roundId }, 'warn');
      }
    }

    const params: string[] = ['from=hole'];
    if (debug) params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/summary${query}`);
  }, [canMutateRoundStatus, debug, logWithContext, roundId, router, socketClient]);

  return {
    finishRound,
    navigateToHole,
  };
};
